<script setup lang="ts">
import { projectConceptSetupInjectionKey } from '@/modules/nexus/injectionKeys'
import { useRouter, useRoute } from 'vue-router'
import { inject, ref, computed, onBeforeMount, watch } from 'vue'
import { useStoreProjectSetup } from '@/stores/project-setup'
import { useStoreFacility } from '@/stores/facility'
import { useToolStore } from '@/stores/tool'
import { appToast } from '@/shared/components/toast/toast'

import ProjectConceptDetails from '@/app/concept/setup/ProjectConceptDetails.vue'
import CollectionSetupFooter from '@/app/concept/setup/CollectionSetupFooter.vue'
import { setupSaveStatus, stepAfter } from '@/app/concept/setup/collection-setup-steps'
import Button from '@/shared/components/Button.vue'
import Loader from '@/shared/components/Loader.vue'
import { useAssertModuleAccess } from '@/shared/composables/useAssertModuleAccess'
import { useTerminology } from '@/shared/composables/useTerminology'

import type { ProjectConceptSetupProvider } from '@/pages/(with-statusbar)/projects/create/[[projectUuid]].vue'

const router = useRouter()
const route = useRoute()
const storeProjectSetup = useStoreProjectSetup()
const { terms } = useTerminology()

// Use injection like other pages in the flow
const {
  state,
  createProjectDto,
  createProjectSetupDto,
  resetState,
  hydrateFromStore,
  persistLocalSetup
} = inject<ProjectConceptSetupProvider>(projectConceptSetupInjectionKey)!

// FIXME: Temporary workaround, remove it when new setup flow is up and running
import { useServiceFacility } from '@/api/services/facility.service'
import { useServiceProject } from '@/api/services/project.service'
import { ConceptType, OperationalModel } from '§M/types/enumerated.type'

// Computed properties for project state
const isExistingProject = computed(() => !!storeProjectSetup.currentProject)

const { createProject, createProjectSetup, updateProjectSetup, readProject } = useServiceProject()
const { syncFacilitiesForProject } = useServiceFacility()

// Reactive project loading
const projectUuid = computed(() =>
  route.params && 'projectUuid' in route.params && typeof route.params.projectUuid === 'string'
    ? route.params.projectUuid
    : undefined
)

// Direct-URL access guard: when loading an existing draft, redirect to
// /no-module-access if the project's operational_model is not subscribed.
// Keyed on the route projectUuid (not storeProjectSetup.currentProject, which the
// create flow populates itself) and read from the loaded project (not local state,
// which resetState() reverts to the NEXUS default). A new collection carries no
// projectUuid and adopts the tool selected before the collections list, which the
// navigation guard has already checked against the org's modules.
useAssertModuleAccess(() =>
  projectUuid.value ? storeProjectSetup.currentProject?.project?.operational_model ?? null : null
)

// Create reactive project loader
const projectLoader = computed(() => {
  if (!projectUuid.value) return null
  return readProject(projectUuid.value)
})

// Initialize project loading when UUID is available
onBeforeMount(() => {
  // Trigger initial load if we have a project UUID
  if (projectUuid.value && projectLoader.value) {
    projectLoader.value.fetchData().catch((error) => {
      console.error('Failed to load project:', error)
    })
  } else {
    // New project: Reset state and clear any previous project data
    resetState()
    storeProjectSetup.clearCurrentProject()
  }
})

// Reactive loading state based on project loader
const isLoadingProject = computed(() => {
  return projectLoader.value?.pending.value ?? false
})

// Watch for project data changes and hydrate form state reactively
watch(
  () => storeProjectSetup.currentProject,
  (currentProject, previousProject) => {
    if (currentProject && projectUuid.value && currentProject.project.uuid === projectUuid.value) {
      // Existing project: Hydrate from store
      hydrateFromStore()
    } else if (!currentProject && !projectUuid.value) {
      // No project and no UUID: ensure clean state for new project
      resetState()
    }
  },
  { immediate: true, flush: 'sync' }
)

// Watch for route changes to handle navigation between projects
watch(projectUuid, (newUuid, oldUuid) => {
  if (newUuid !== oldUuid) {
    if (newUuid && typeof newUuid === 'string') {
      // Load new project
      const loader = readProject(newUuid)
      loader.fetchData().catch((error) => {
        console.error('Failed to load project on route change:', error)
      })
    } else {
      // No UUID, reset for new project
      resetState()
      storeProjectSetup.clearCurrentProject()
    }
  }
})

// Loading and error states
const isCreating = ref(false)
const validationErrors = ref<string[]>([])

// An Axios facility takes its coordinates and time zone from a location
const needsLocation = computed(
  () => state.value.operationalModel === OperationalModel.AXIOS && !state.value.facility.uuid
)

// Validation computed
const isFormValid = computed(() => {
  const hasTitle = Boolean(state.value.title?.trim())

  return hasTitle && !needsLocation.value
})

const nextButtonTooltip = computed(() => {
  if (!state.value.title?.trim()) return `Please fill in the ${terms.value.project} title`
  if (needsLocation.value) return 'Please choose a location'
  return undefined
})

const validateForm = (): boolean => {
  const errors: string[] = []

  if (!state.value.title?.trim()) {
    errors.push(`${terms.value.Project} title is required`)
  }
  if (needsLocation.value) {
    errors.push('A location is required')
  }

  validationErrors.value = errors
  return errors.length === 0
}

const doCreateProject = async () => {
  if (!validateForm()) return

  isCreating.value = true
  const dto = createProjectDto()

  try {
    const { data, error } = await createProject(dto)

    if (data) {
      // NEXUS projects don't need facility connections - they create new facilities
      resetState()
      router.push({
        name: '/(with-statusbar)/projects/[projectUuid]/concepts/',
        params: { projectUuid: data.project.uuid }
      })
    } else {
      validationErrors.value = [error?.message || 'Failed to create project']
    }
  } catch (err) {
    validationErrors.value = [`An unexpected error occurred while creating the ${terms.value.project}`]
    console.error('Create project error:', err)
  } finally {
    isCreating.value = false
  }
}

const doCreateProjectSetup = async () => {
  if (!validateForm()) return

  isCreating.value = true

  try {
    let data, error

    // Captured before the save: the setup payload carries no location, so the
    // response still reports the old link. Storing it re-runs hydrateFromStore
    // synchronously, which refills the facility from that stale response and this
    // browser's copy of the previous save, and would discard the user's changes.
    const facility = { ...state.value.facility }

    if (isExistingProject.value) {
      // Update existing project
      const projectUuid = storeProjectSetup.currentProject?.project?.uuid
      if (!projectUuid) {
        validationErrors.value = ['Project UUID not found for existing project']
        return
      }

      const updateDto = {
        title: state.value.title,
        description: state.value.description,
        operational_model: state.value.operationalModel,
        status: setupSaveStatus(storeProjectSetup.currentProject?.project?.status)
      }

      const result = await updateProjectSetup(projectUuid, updateDto)
      data = result.data
      error = result.error
    } else {
      // Create new project
      const dto = createProjectSetupDto()
      const result = await createProjectSetup(dto)
      data = result.data
      error = result.error
    }

    state.value.facility = facility

    if (data) {
      persistLocalSetup()

      // Reconcile the link on every save, not only on creation: the step bar lets the
      // user come back here and choose another location.
      const linkedFacilityIds = (data.project.facilities ?? []).map((it) => it.uuid)
      if (facility.uuid && (linkedFacilityIds.length !== 1 || linkedFacilityIds[0] !== facility.uuid)) {
        await syncFacilitiesForProject([facility.uuid], linkedFacilityIds, data.project.uuid)
        // The response was built before the link changed; the next step hydrates
        // from the stored snapshot, so re-read it to match what was just persisted.
        await readProject(data.project.uuid).fetchData()
      }

      router.push({
        name: stepAfter('/(with-statusbar)/projects/create/[[projectUuid]]/')!.routeName,
        params: { projectUuid: data.project.uuid }
      })
    } else {
      validationErrors.value = [error?.message || 'Failed to process project setup']
    }
  } catch (err) {
    validationErrors.value = [`An unexpected error occurred while processing the ${terms.value.project} setup`]
    console.error('Process project setup error:', err)
  } finally {
    isCreating.value = false
  }
}

// A new Axios facility is only set up from a free location's card; any other entry goes back to the cards
definePage({
  beforeEnter: async (to) => {
    if (useToolStore().selectedTool !== OperationalModel.AXIOS) return true
    if ('projectUuid' in to.params && to.params.projectUuid) return true

    const storeFacility = useStoreFacility()
    if (!storeFacility.facilities.length) await useServiceFacility().readAllFacilities().fetchData()
    const isFreeLocation = storeFacility.facilities.some(
      (facility) => facility.uuid === to.query.location && !facility.projects?.length
    )
    if (isFreeLocation) return true

    appToast.warning('Set up a facility from its location card')
    return { name: '/(with-statusbar)/projects/' }
  }
})
</script>

<template>
  <div
    v-if="isLoadingProject"
    class="flex justify-center p-4"
  >
    <Loader />
  </div>

  <ProjectConceptDetails
    v-else-if="state"
    v-model:title="state.title"
    v-model:description="state.description"
    v-model:operational-model="state.operationalModel"
    :is-existing-project="isExistingProject"
  />

  <!-- Validation errors -->
  <div
    v-if="validationErrors.length > 0"
    class="border-destructive bg-accent rounded-lg border p-4"
  >
    <div class="flex">
      <div class="ml-3">
        <h3 class="text-destructive text-sm font-medium">Please fix the following errors:</h3>
        <div class="text-foreground mt-2 text-sm">
          <ul class="list-disc space-y-1 pl-5">
            <li
              v-for="error in validationErrors"
              :key="error"
            >
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- No Previous on the first step: a permanently disabled control would imply a
       wizard the single-step NEXUS flow does not have. -->
  <CollectionSetupFooter>
    <Button
      v-if="state.operationalModel === OperationalModel.AXIOS"
      label="Cancel"
      severity="secondary"
      :disabled="isCreating"
      @click="router.push({ name: '/(with-statusbar)/projects/' })"
    />
    <!-- Show "Next" button only for AXIOS projects -->
    <Button
      v-if="state.operationalModel === OperationalModel.AXIOS"
      v-tooltip="nextButtonTooltip"
      label="Next"
      :loading="isCreating"
      :disabled="!isFormValid || isCreating"
      @click="doCreateProjectSetup"
    />

    <!-- Show the completion button only for NEXUS projects.
         Each operational model gets its own explicit flow; do not use a
         "not AXIOS" catch-all, or future models would fall into this one. -->
    <Button
      v-if="state.operationalModel === OperationalModel.NEXUS"
      v-tooltip="nextButtonTooltip"
      :label="`Complete ${terms.project} setup`"
      :loading="isCreating"
      :disabled="!isFormValid || isCreating"
      @click="doCreateProject"
    />
  </CollectionSetupFooter>
</template>
